import RichText from '@eneco/components/RichText/RichText';
import { Button, Checkbox, Heading, InputUsage, Stack } from '@eneco/sparky';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { PriceSimulationRenderingExtended } from '@sitecore/types/manual/be/PriceSimulationExtended';
import { Controller, useFormContext } from 'react-hook-form';

import { PriceSimulationFormSchema } from '../schemas/priceSimulationFormSchema';
import { FlowChildProps } from '../types/PriceSimulationSteps';
import { getTrackableFormValues } from '../utils/getTrackableFormValues';

export const PeakEnergyConsumptionStep = ({ nextMainStep }: FlowChildProps) => {
  const { fields } = useContent<PriceSimulationRenderingExtended>();
  const { trigger, setValue, control, formState, getValues } = useFormContext<PriceSimulationFormSchema>();
  const { trackEventBE } = useTracking();

  const onNextClick = async () => {
    const isValid = await trigger('peakEnergyConsumption');
    if (!isValid) {
      return;
    }
    trackEventBE('pbt_calculation_peak', {
      status: 'click',
      data: getTrackableFormValues(getValues()),
    });
    nextMainStep();
  };

  const handlePeakEnergyConsumptionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    fieldOnChange: (value: string) => void,
  ) => {
    const value = event.target.value;
    const separatorMatch = value.match(/[.,]/);
    // Cap the value to 2 decimal places
    const cappedValue = separatorMatch
      ? value.split(separatorMatch[0])[0] + separatorMatch[0] + value.split(separatorMatch[0])[1].slice(0, 2)
      : value;
    fieldOnChange(cappedValue);
    if (cappedValue !== fields.capacityRateStep.defaultPeakEnergyConsumptionNumberText.value) {
      setValue('isCheckedUseDefaultPeakConsumption', false);
    }
  };

  return (
    <>
      <Stack direction="column" gap="2">
        <Heading as="h1" size="M">
          {fields.capacityRateStep.title.value}
        </Heading>
        <div>
          <RichText html={fields.capacityRateStep.subTitle.value} />
        </div>
      </Stack>
      <Stack gap={10}>
        <Stack gap={4}>
          <Controller
            control={control}
            name={'peakEnergyConsumption'}
            render={({ field }) => (
              <InputUsage
                {...field}
                label={fields.capacityRateStep.capacityRateFormField.value.label}
                placeholder={fields.capacityRateStep.capacityRateFormField.value.placeholder}
                hint={fields.capacityRateStep.capacityRateFormField.value.hint}
                type="energy"
                unit="kW"
                onChange={event => handlePeakEnergyConsumptionChange(event, field.onChange)}
                {...(formState.errors.peakEnergyConsumption && {
                  error: formState.errors.peakEnergyConsumption.message,
                })}
              />
            )}
          />
          <Controller
            control={control}
            name={'isCheckedUseDefaultPeakConsumption'}
            render={({ field }) => (
              <Checkbox
                {...field}
                label={fields.capacityRateStep.capcityRateUnknownFormField.value.label}
                hint={<RichText html={fields.capacityRateStep.capcityRateUnknownFormField.value.hint} />}
                error={formState.errors.isCheckedUseDefaultPeakConsumption?.message}
                isChecked={field.value}
                onChange={event => {
                  if (event.target.checked) {
                    setValue(
                      'peakEnergyConsumption',
                      fields.capacityRateStep.defaultPeakEnergyConsumptionNumberText.value,
                    );
                  }
                  field.onChange(event.target.checked);
                }}
              />
            )}
          />
        </Stack>
        <div>
          <RichText html={fields.capacityRateStep.description.value} />
        </div>
      </Stack>
      <Button onClick={onNextClick}>{fields.navigation.nextButtonText.value}</Button>
    </>
  );
};
